package com.sddamico.swigmore.util

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.sddamico.swigmore.api.ApiException
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.launch

const val UNREACHABLE_MESSAGE = "Couldn't reach the poll. Check your connection and try again."

/** The server's message for an [ApiException]; anything else means the request never got an answer. */
fun Throwable.errorText(): String = (this as? ApiException)?.errorMessage ?: UNREACHABLE_MESSAGE

/** Data fetched from the API, as a [LoadState] that [reload] refreshes. */
class ApiResource<T> internal constructor(
  private val fetch: suspend () -> T,
  initial: LoadState<T> = LoadState.Loading,
  private val onLoaded: (T) -> Unit = {},
) {
  var state: LoadState<T> by mutableStateOf(initial)
    private set

  /** The loaded value, or null while loading or after a failed first load. */
  val value: T? get() = (state as? LoadState.Ready)?.value

  /** Fetches again. Once something has loaded, a failure keeps it on screen and throws for the caller to report. */
  suspend fun reload() {
    state = try {
      LoadState.Ready(fetch().also(onLoaded))
    } catch (e: CancellationException) {
      throw e
    } catch (e: Throwable) {
      if (state is LoadState.Ready) throw e
      LoadState.Failed(e.errorText())
    }
  }
}

/** An [ApiResource] loaded on first composition and again whenever [key] changes. */
@Composable
fun <T> rememberApiResource(key: Any? = Unit, fetch: suspend () -> T): ApiResource<T> {
  val resource = remember(key) { ApiResource(fetch) }
  LaunchedEffect(resource) { resource.reload() }
  return resource
}

private val resourceCache = mutableMapOf<CachedResource<*>, Any?>().also { forgetOnSignOut(it::clear) }

/**
 * An [ApiResource] for [key] that a revisit starts from the value it last loaded, refreshed in place,
 * so moving between tabs doesn't blank the page. For read-only screens: an editor seeded from the
 * cached value would keep it after the fresh one arrives.
 */
@Composable
fun <T> rememberCachedResource(key: CachedResource<T>): ApiResource<T> {
  val resource = remember(key) {
    @Suppress("UNCHECKED_CAST")
    val cached = if (key in resourceCache) LoadState.Ready(resourceCache[key] as T) else LoadState.Loading
    ApiResource(key::fetch, cached) { resourceCache[key] = it }
  }
  LaunchedEffect(resource) {
    // A failed refresh leaves the cached value on screen; the next visit tries again.
    try {
      resource.reload()
    } catch (e: CancellationException) {
      throw e
    } catch (_: Throwable) {
    }
  }
  return resource
}

/** A user-triggered request: [busy] while it runs, and the [error] it ended with. */
class Action internal constructor(private val scope: CoroutineScope) {
  var busy by mutableStateOf(false)
    private set
  var error by mutableStateOf<String?>(null)

  /**
   * Runs [block], clearing the last error first. Ignored while a run is in flight, so disable its controls on [busy].
   * A run is a write unless [writes] says otherwise, so it drops the [rememberCachedResource] values it may have
   * outdated: the next tab loads fresh behind its skeleton instead of showing, say, "Not yet voted" right after a vote.
   * Dropped before the write, so whatever [block] reloads after it stays cached.
   */
  fun run(writes: Boolean = true, block: suspend () -> Unit) {
    if (busy) return
    busy = true
    error = null
    if (writes) resourceCache.clear()
    scope.launch {
      try {
        block()
      } catch (e: CancellationException) {
        throw e
      } catch (e: Throwable) {
        error = e.errorText()
      } finally {
        busy = false
      }
    }
  }
}

@Composable
fun rememberAction(): Action {
  val scope = rememberCoroutineScope()
  return remember { Action(scope) }
}
